import React, { Component } from "react";
import { connectStore } from "redux-box";
import { module as ui } from "@st/ui";
import { module as platform } from "@st/platform";
import ContentPolicyRuleBuilder from "./ContentPolicyRuleBuilder";
import {
  FormContainer,
  FormTitle,
  FormGroup,
  Label,
  Input,
  Textarea,
  SectionLabel,
  SubmitButton,
  ErrorText,
} from "./PlatformForm.styled";

class PlatformForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: "",
      content_policy: {},
      theme_config_text: "",
      hidden_features_text: "",
      theme_config_error: "",
      hidden_features_error: "",
    };
  }

  componentDidMount() {
    this.syncFormState();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.platform.active_platform !== this.props.platform.active_platform ||
      prevProps.platform.is_edit_platform !== this.props.platform.is_edit_platform
    ) {
      this.syncFormState();
    }
  }

  syncFormState = () => {
    const { active_platform } = this.props.platform;
    const themeConfig = active_platform && active_platform.theme_config
      ? active_platform.theme_config
      : null;
    this.setState({
      name: active_platform ? active_platform.name : "",
      content_policy: active_platform && active_platform.content_policy
        ? active_platform.content_policy
        : {},
      theme_config_text: themeConfig
        ? JSON.stringify(themeConfig, null, 2)
        : "",
      hidden_features_text: active_platform && active_platform.hidden_features && active_platform.hidden_features.length
        ? JSON.stringify(active_platform.hidden_features, null, 2)
        : "",
      theme_config_error: "",
      hidden_features_error: "",
    });
  };

  handleNameChange = (e) => {
    this.setState({ name: e.target.value });
  };

  handlePolicyChange = (policy) => {
    this.setState({ content_policy: policy });
  };

  handleThemeConfigChange = (e) => {
    this.setState({ theme_config_text: e.target.value, theme_config_error: "" });
  };

  handleHiddenFeaturesChange = (e) => {
    this.setState({ hidden_features_text: e.target.value, hidden_features_error: "" });
  };

  validate = () => {
    const { name, theme_config_text, hidden_features_text } = this.state;
    if (!name || name.trim().length < 2) return "Platform name requires at least 2 characters.";
    if (theme_config_text && theme_config_text.trim()) {
      try {
        JSON.parse(theme_config_text);
      } catch (_) {
        this.setState({ theme_config_error: "Theme Config must be valid JSON." });
        return "Theme Config must be valid JSON.";
      }
    }
    if (hidden_features_text && hidden_features_text.trim()) {
      try {
        const parsed = JSON.parse(hidden_features_text);
        if (!Array.isArray(parsed)) {
          this.setState({ hidden_features_error: "Hidden Features must be a JSON array, e.g. [\"news\", \"quiz\"]" });
          return "Hidden Features must be a JSON array.";
        }
      } catch (_) {
        this.setState({ hidden_features_error: "Hidden Features must be valid JSON." });
        return "Hidden Features must be valid JSON.";
      }
    }
    return null;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const validationError = this.validate();
    if (validationError) {
      this.props.platform.setPlatformError(validationError);
      return;
    }

    const { name, content_policy, theme_config_text, hidden_features_text } = this.state;
    const { is_edit_platform: isEdit, active_platform } = this.props.platform;

    const payload = {
      name,
      content_policy,
      hidden_features: hidden_features_text && hidden_features_text.trim()
        ? JSON.parse(hidden_features_text)
        : [],
      theme_config: theme_config_text && theme_config_text.trim()
        ? JSON.parse(theme_config_text)
        : null,
    };

    if (isEdit && active_platform) {
      this.props.platform.updatePlatform({ id: active_platform.id, payload });
    } else {
      this.props.platform.createPlatform(payload);
    }
  };

  render() {
    const {
      is_edit_platform: isEdit,
      platforms_submitting: submitting,
      platforms_error: error,
      content_policy_schema: schema,
    } = this.props.platform;
    const { name, content_policy, theme_config_text, hidden_features_text, theme_config_error, hidden_features_error } = this.state;

    return (
      <FormContainer onSubmit={this.handleSubmit}>
        <FormTitle>{isEdit ? "Edit Platform" : "New Platform"}</FormTitle>

        <FormGroup>
          <Label>Platform Name *</Label>
          <Input
            type="text"
            value={name}
            onChange={this.handleNameChange}
            placeholder="e.g. Greenwood Public School Platform"
            required
          />
        </FormGroup>

        <FormGroup>
          <SectionLabel>Hidden Features (JSON)</SectionLabel>
          <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)", marginBottom: "12px" }}>
            JSON array of feature names to hide. Leave empty for no restrictions.
          </div>
          <Textarea
            value={hidden_features_text}
            onChange={this.handleHiddenFeaturesChange}
            placeholder={'["news", "quiz"]'}
            style={{ minHeight: "72px" }}
          />
          {hidden_features_error && (
            <div style={{ color: "#cf1322", fontSize: "12px", marginTop: "6px" }}>
              {hidden_features_error}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <SectionLabel>Theme Config (JSON)</SectionLabel>
          <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)", marginBottom: "12px" }}>
            Optional. Per-device theme overrides as JSON (keys: <code>android</code>, <code>ios</code>, <code>web</code>).
            Leave empty for no theme override.
          </div>
          <Textarea
            value={theme_config_text}
            onChange={this.handleThemeConfigChange}
            placeholder={'{\n  "android": { "theme": {}, "assets": "" },\n  "ios": { "theme": {}, "assets": "" }\n}'}
          />
          {theme_config_error && (
            <div style={{ color: "#cf1322", fontSize: "12px", marginTop: "6px" }}>
              {theme_config_error}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <SectionLabel>Content Policy Rules</SectionLabel>
          <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)", marginBottom: "12px" }}>
            Add rules to restrict what content children on this platform see.
            Leave empty for unrestricted access.
          </div>
          <ContentPolicyRuleBuilder
            policy={content_policy}
            schema={schema}
            onChange={this.handlePolicyChange}
          />
        </FormGroup>

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton type="submit" disabled={submitting}>
          {submitting ? "Saving..." : "Save"}
        </SubmitButton>
      </FormContainer>
    );
  }
}

export default connectStore({
  ui,
  platform,
})(PlatformForm);
